import _debounce from "lodash/debounce";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon, Input } from "semantic-ui-react";

import "components/NavBarSearch/NavBarSearch.scss";

type SearchFilterProps = {
  value: string;
  debounceTime?: number;
  handleSearchChange: (value: string) => void;
};

const DEBOUNCE_TIME = 500;

export const SearchFilter = ({ value, handleSearchChange, debounceTime = DEBOUNCE_TIME }: SearchFilterProps) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState(value);
  const handleSearchChangeRef = useRef(handleSearchChange);
  const lastSentTerm = useRef(value);

  handleSearchChangeRef.current = handleSearchChange;

  const debouncedHandleSearchChange = useMemo(
    () =>
      _debounce((term: string) => {
        lastSentTerm.current = term;
        handleSearchChangeRef.current(term);
      }, debounceTime),
    [debounceTime]
  );

  const handleOnChange = (searchText: string) => {
    setSearchTerm(searchText);
    debouncedHandleSearchChange(searchText);
  };

  useEffect(() => {
    if (value !== lastSentTerm.current) {
      debouncedHandleSearchChange.cancel();
      lastSentTerm.current = value;
      setSearchTerm(value);
    }

    return () => debouncedHandleSearchChange.cancel();
  }, [value, debouncedHandleSearchChange]);

  return (
    <div className="NavBarSearch">
      <Input
        icon={<Icon className="magnifying glass" />}
        iconPosition="left"
        placeholder={`${t("v8_search", "Search")}...`}
        className="nav-bar-search"
        onChange={e => handleOnChange(e.target.value)}
        value={searchTerm}
      />
    </div>
  );
};
